/* ============================================================
   ATHLO+ — Motion & interaction layer
   GSAP + ScrollTrigger + Lenis smooth scroll
   ============================================================ */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGSAP = typeof gsap !== 'undefined';
  const finePointer = window.matchMedia('(pointer: fine)').matches;

  if (hasGSAP && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
  }

  /* --------------------------------------------------------
     SMOOTH SCROLL (Lenis) + ScrollTrigger sync
  -------------------------------------------------------- */
  let lenis = null;
  function initLenis() {
    if (reduceMotion || typeof Lenis === 'undefined') return;
    lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 1, smoothWheel: true });
    window.__lenis = lenis;
    if (hasGSAP) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
    }
  }

  // Anchor links -> smooth scroll
  document.addEventListener('click', function (e) {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute('href');
    if (id === '#' || id === '#top') {
      e.preventDefault();
      lenis ? lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: 'smooth' });
      closeMobile();
      return;
    }
    let el = null;
    try { el = document.querySelector(id); } catch (_) { el = null; } // non-selector hashes (#a?x=1) would throw
    if (el) {
      e.preventDefault();
      closeMobile();
      lenis ? lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: 'smooth' });
      // Skip link must move keyboard focus to the target, not just scroll — otherwise the
      // next Tab lands back in the nav, defeating the whole purpose. (a11y)
      if (a.classList.contains('skip-link')) { try { el.focus({ preventScroll: true }); } catch (_) {} }
    }
  });

  /* --------------------------------------------------------
     PRELOADER
  -------------------------------------------------------- */
  function runPreloader(done) {
    const pl = document.getElementById('preloader');
    const bar = document.getElementById('pl-bar');
    const count = document.getElementById('pl-count');
    let finished = false;
    function finish() { if (finished) return; finished = true; done(); }
    if (!pl) { finish(); return; }
    // === ADDON (perf, first-visit-only): play the FORGE loader ONCE per session. On the
    // first homepage load of a session it plays; on refreshes, back-to-home and repeat
    // homepage visits it skips straight to the revealed state (the SAME path reduced-motion
    // uses), so returning-within-session users don't re-pay the intro. Resets when the tab/
    // browser closes. sessionStorage is wrapped: private mode throwing => default to playing. ===
    let loaderSeen = false;
    try { loaderSeen = sessionStorage.getItem('hub:loader') === '1'; } catch (e) {}
    if (reduceMotion || !hasGSAP || loaderSeen) {
      pl.style.display = 'none';
      finish();
      return;
    }
    try { sessionStorage.setItem('hub:loader', '1'); } catch (e) {}
    // Failsafe: never let the preloader trap the page (e.g. a backgrounded
    // tab where requestAnimationFrame — and therefore GSAP — is paused).
    const failsafe = setTimeout(function () { pl.style.display = 'none'; finish(); }, 6500);

    // === ADDON: cinematic loader (remove this block + runCinematic/runPowerup to revert) ===
    // "cinematic" now drives the POWER-UP badge loader (runPowerup). runCinematic
    // is the older scanline reveal, kept below for easy revert.
    if (pl.dataset.loader === 'cinematic') { runPowerup(pl, bar, count, finish, failsafe); return; }
    // === END ADDON ===

    const obj = { v: 0 };
    const tl = gsap.timeline({ onComplete: function () {
      gsap.to(pl, { yPercent: -100, duration: 0.9, ease: 'power3.inOut', delay: 0.15,
        onStart: finish,
        onComplete: function () { clearTimeout(failsafe); pl.style.display = 'none'; }
      });
    }});
    tl.to(obj, {
      v: 100, duration: 1.6, ease: 'power2.inOut',
      onUpdate: function () {
        const v = Math.round(obj.v);
        if (bar) bar.style.transform = 'scaleX(' + (v / 100) + ')';
        if (count) count.textContent = v + '%';
      }
    });
  }

  /* === ADDON: IGNITION v3 badge loader ===
     Four phases:
     1) BOOT — crosshair hairlines draw through centre, corner brackets snap
        around the badge, mono boot lines decode in (scramble → text).
     2) CHARGE — a canvas particle field streams lime energy into the badge
        while the charge ring traces its frame (the frame IS the progress bar),
        the badge brightens (--pw-lit 0→1), the oversized counter runs 0→100
        (pulsing at each quartile) and the rotating words decode in.
     3) OVERLOAD — at 100% the particles blast outward, a bolt-flash blooms,
        lightning crackles, the badge glitches (clip-slices) and the whole
        stage micro-shakes while two shockwave rings ripple out.
     4) REVEAL — the badge zooms through the camera as the columns wipe up
        trailing lime edges, handing over to the hero intro. */

  /* Scramble-decode: fills el with glitch glyphs that resolve into `text`. */
  function pwDecode(el, text, dur) {
    if (!el) return;
    const glyphs = '#%/\\<>+=*01';
    const len = text.length;
    const t0 = performance.now();
    (function step() {
      const p = Math.min(1, (performance.now() - t0) / (dur * 1000));
      const n = Math.floor(p * len);
      let s = text.slice(0, n);
      for (let i = n; i < len; i++) s += (text[i] === ' ') ? ' ' : glyphs[(Math.random() * glyphs.length) | 0];
      el.textContent = s;
      if (p < 1) requestAnimationFrame(step);
    })();
  }

  /* Energy particle field: streaks converge on the badge while charging, then
     blast outward on burst(). Self-stops if the preloader is hidden (failsafe). */
  function pwParticles(canvas, pl) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w, h, cx, cy;
    function size() {
      w = canvas.width = Math.floor(window.innerWidth * dpr);
      h = canvas.height = Math.floor(window.innerHeight * dpr);
      cx = w / 2; cy = h / 2;
    }
    size();
    window.addEventListener('resize', size);

    const N = 90; // calmed from 160 (2026-07-18, boss ask: cleaner frame)
    const P = [];
    function spawn(p) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.max(w, h) * (0.35 + Math.random() * 0.45);
      p.x = cx + Math.cos(a) * r;
      p.y = cy + Math.sin(a) * r;
      p.sp = (0.55 + Math.random() * 1.3) * dpr;
      p.o = 0.18 + Math.random() * 0.55;
      p.vx = 0; p.vy = 0;
      p.hot = Math.random() < 0.22; // white-hot sparks among the lime
    }
    for (let i = 0; i < N; i++) { const p = {}; spawn(p); P.push(p); }

    let energy = 0, mode = 'charge', alive = true;
    const core = () => Math.min(w, h) * 0.11; // "absorbed" radius around the badge
    function tick() {
      if (!alive) return;
      if (!pl.isConnected || pl.style.display === 'none') { alive = false; return; }
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      for (let i = 0; i < N; i++) {
        const p = P[i];
        const dx = cx - p.x, dy = cy - p.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        let px = p.x, py = p.y;
        if (mode === 'charge') {
          const v = p.sp * (1.5 + energy * 14);
          p.x += (dx / d) * v;
          p.y += (dy / d) * v;
          if (d < core()) { spawn(p); continue; }
        } else { // burst: sparks fly out then fall like embers
          p.x += p.vx; p.y += p.vy;
          p.vx *= 0.965; p.vy = p.vy * 0.965 + 0.42 * dpr;
          p.o *= 0.955;
          if (p.o < 0.01) continue;
        }
        const a = (mode === 'charge') ? p.o * (0.25 + energy * 0.75) : p.o;
        ctx.strokeStyle = p.hot ? 'rgba(240,255,214,' + a.toFixed(3) + ')' : 'rgba(183,255,0,' + a.toFixed(3) + ')';
        ctx.lineWidth = (0.9 + p.o * 1.3) * dpr;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    return {
      setEnergy: function (v) { energy = v; },
      burst: function () {
        mode = 'burst';
        for (let i = 0; i < N; i++) {
          const p = P[i];
          const dx = p.x - cx, dy = p.y - cy;
          const d = Math.sqrt(dx * dx + dy * dy) || 1;
          const v = (6 + Math.random() * 16) * dpr;
          p.vx = (dx / d) * v; p.vy = (dy / d) * v;
          p.o = 0.35 + Math.random() * 0.55;
        }
      },
      stop: function () { alive = false; window.removeEventListener('resize', size); ctx.clearRect(0, 0, w, h); }
    };
  }

  function runPowerup(pl, bar, count, finish, failsafe) {
    const words = pl.querySelectorAll('.cine-words span');
    const badge = pl.querySelector('#pw-badge');
    const img = pl.querySelector('#ign-logo');
    const ring = pl.querySelector('#pw-ring');
    const flash = pl.querySelector('#ign-flash');
    const wave = pl.querySelector('#pw-wave');
    const wave2 = pl.querySelector('#pw-wave2');
    const bolt = pl.querySelector('#pw-bolt');
    const plus = pl.querySelector('.ign-plus');
    const bigCount = pl.querySelector('.ign-count');
    const meta = pl.querySelector('.cine-meta');
    const stage = pl.querySelector('.ign-stage');
    const progress = pl.querySelector('.ign-progress');
    const cols = pl.querySelectorAll('.ign-bg span');
    const crossH = pl.querySelector('.pw-cross__h');
    const crossV = pl.querySelector('.pw-cross__v');
    const corners = pl.querySelectorAll('.pw-corner');
    const bootLines = pl.querySelectorAll('.pw-boot span');
    const canvas = pl.querySelector('#pw-canvas');
    // FORGE v4 additions: molten pour edge, giant counter digits, white blast, lens streak
    const pour = pl.querySelector('#pw-pour');
    let pourW = 0; // badge width, read once (not per frame) so moving the pour line never forces layout
    const countN = pl.querySelector('#ign-count-n');
    const blast = pl.querySelector('#pw-blast');
    const streak = pl.querySelector('#pw-streak');
    const wordText = [];
    words.forEach(function (s) { wordText.push(s.textContent); });

    const px = (canvas && !window.HUB_LITE) ? pwParticles(canvas, pl) : null;   // lite: no loader particle field

    gsap.set(words, { yPercent: 120, opacity: 0 });
    if (words[0]) gsap.set(words[0], { yPercent: 0, opacity: 1 });
    if (bigCount) gsap.set(bigCount, { yPercent: 30, opacity: 0 });
    if (meta) gsap.set(meta, { opacity: 0 });
    if (badge) { gsap.set(badge, { scale: 0.9, opacity: 0 }); badge.style.setProperty('--pw-lit', '0'); }
    if (ring) ring.style.strokeDashoffset = 100;

    // Ambient: the ghost plus slowly rotates/breathes behind the badge.
    var ambient = null;
    if (plus) {
      gsap.set(plus, { xPercent: -50, yPercent: -50, scale: 0.94, rotate: 0, left: '50%', top: '50%', transformOrigin: '50% 50%' });
      ambient = gsap.to(plus, { rotate: 90, scale: 1.02, duration: 5, ease: 'none' });
    }

    const obj = { v: 0 };
    let wi = 0;
    let quart = 0;
    const tl = gsap.timeline();

    // ---- 1) BOOT: crosshair draws, the wordmark SLAMS in from the camera
    // (scale+blur impact, not a fade), brackets snap in, boot lines decode
    if (crossH) tl.fromTo(crossH, { scaleX: 0 }, { scaleX: 1, duration: 0.7, ease: 'expo.out' }, 0.05);
    if (crossV) tl.fromTo(crossV, { scaleY: 0 }, { scaleY: 1, duration: 0.7, ease: 'expo.out' }, 0.1);
    if (badge) tl.fromTo(badge, { opacity: 0, scale: 1.6, filter: 'blur(18px)' },
      { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.55, ease: 'power4.out' }, 0.15);
    if (stage) tl.to(stage, { keyframes: { x: [0, -5, 4, -2, 0], y: [0, 3, -3, 1, 0] }, duration: 0.3, ease: 'power1.out' }, 0.62);
    if (corners.length) tl.fromTo(corners, { opacity: 0, scale: 1.8 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power3.out', stagger: 0.05 }, 0.55);
    bootLines.forEach(function (line, i) {
      tl.to(line, {
        opacity: 1, duration: 0.05,
        onStart: function () { pwDecode(line, line.dataset.boot || '', 0.5); }
      }, 0.2 + i * 0.18);
    });
    if (meta) tl.to(meta, { opacity: 1, duration: 0.5 }, 0.4);
    if (bigCount) tl.to(bigCount, { yPercent: 0, opacity: 1, duration: 0.6, ease: 'power4.out' }, 0.45);

    // ---- 2) CHARGE 0→100: molten chrome POURS into the letterforms (the
    // wordmark IS the progress bar), pour-edge scanline rides the fill line,
    // giant chrome counter climbs, particles stream in, hairline ring traces
    tl.to(obj, {
      v: 100, duration: 1.2, ease: 'power1.inOut',   /* perf 2026-09-07: was 2.3 — shorten cold-load LCP */
      onUpdate: function () {
        const v = Math.round(obj.v);
        if (ring) ring.style.strokeDashoffset = (100 - obj.v);
        if (badge) {
          badge.style.setProperty('--pw-lit', (0.22 + 0.78 * (obj.v / 100)).toFixed(3));
          badge.style.setProperty('--pw-fill', obj.v.toFixed(2) + '%');
        }
        if (pour) {
          // ride the molten fill line across the wordmark (5%..95% of the badge). transform, not left:
          // `left` re-laid-out the badge every frame and was the homepage's entire CLS (~0.10, 2026-10-13).
          if (!pourW && badge) pourW = badge.clientWidth;
          pour.style.transform = 'translateX(' + (0.009 * obj.v * pourW).toFixed(1) + 'px)';
          pour.style.opacity = (obj.v > 1 && obj.v < 99) ? '1' : '0';
        }
        pl.style.setProperty('--pw-energy', (obj.v / 100).toFixed(3));
        if (px) px.setEnergy(obj.v / 100);
        if (bar) bar.style.transform = 'scaleX(' + (obj.v / 100) + ')';
        if (count) count.textContent = v;
        if (countN) countN.textContent = v;
        // Counter pulses as each quartile locks in
        const q = Math.floor(obj.v / 25);
        if (q > quart && q < 4) {
          quart = q;
          if (bigCount) gsap.fromTo(bigCount, { scale: 1 }, { scale: 1.05, yoyo: true, repeat: 1, duration: 0.1, ease: 'power2.out', transformOrigin: '100% 100%' });
        }
        if (words.length > 1) {
          const target = Math.min(words.length - 1, Math.floor(v / (100 / words.length)));
          if (target !== wi) {
            gsap.to(words[wi], { yPercent: -120, opacity: 0, duration: 0.4, ease: 'power2.in' });
            gsap.fromTo(words[target], { yPercent: 120, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5, ease: 'power3.out' });
            pwDecode(words[target], wordText[target], 0.4);
            wi = target;
          }
        }
      }
    }, 0.75);

    // ---- 3) OVERLOAD: white-out blast + anamorphic lens streak + burst +
    // flash + lightning + glitch + shake + shockwaves, all on one frame
    tl.addLabel('ignite', '>');
    tl.add(function () {
      if (px) px.burst();
      // ADDON (liquid-chrome): one-shot flare to full molten metallic + the whole
      // HUD console flashes chrome-white in sympathy on the same beat (see .is-ignited in the-hub.css)
      if (img) img.classList.add('is-ignited');
      pl.classList.add('is-ignited');
      if (badge) {
        badge.classList.add('is-glitch');
        setTimeout(function () { badge.classList.remove('is-glitch'); }, 380);
      }
    }, '>');
    // Every impact element anchors to the 'ignite' label so they hit ONE frame
    if (blast) {
      tl.fromTo(blast, { opacity: 0 }, { opacity: 0.95, duration: 0.07, ease: 'power4.in' }, 'ignite');
      tl.to(blast, { opacity: 0, duration: 0.45, ease: 'power2.out' }, 'ignite+=0.07');
    }
    // Anamorphic lens streak ripping horizontally through the wordmark
    // (immediateRender:false — otherwise the from-state renders at load and the
    // streak sits visible through the whole boot; same for the waves below)
    if (streak) {
      tl.fromTo(streak, { opacity: 1, scaleX: 0.15 }, { scaleX: 1.9, duration: 0.5, ease: 'expo.out', immediateRender: false }, 'ignite+=0.05');
      tl.to(streak, { opacity: 0, duration: 0.3, ease: 'power2.in' }, 'ignite+=0.35');
    }
    if (flash) {
      tl.fromTo(flash, { opacity: 0, scale: 0.5 }, { opacity: 1, scale: 1, duration: 0.28, ease: 'power2.out' }, 'ignite');
      tl.to(flash, { opacity: 0, duration: 0.4, ease: 'power2.in' }, 'ignite+=0.23');
    }
    if (bolt) tl.fromTo(bolt, { opacity: 0 }, { opacity: 1, duration: 0.05, repeat: 5, yoyo: true, ease: 'none' }, 'ignite');
    if (img) tl.fromTo(img, { scale: 1 }, { scale: 1.07, yoyo: true, repeat: 1, duration: 0.16, ease: 'power2.inOut', transformOrigin: '50% 50%' }, 'ignite');
    if (stage) tl.to(stage, { keyframes: { x: [0, -7, 6, -4, 3, 0], y: [0, 4, -5, 3, -2, 0] }, duration: 0.4, ease: 'power1.out' }, 'ignite');
    if (wave) tl.fromTo(wave, { opacity: 0.85, scale: 0.92 }, { opacity: 0, scale: 1.6, duration: 0.75, ease: 'power2.out', immediateRender: false }, 'ignite');
    if (wave2) tl.fromTo(wave2, { opacity: 0.6, scale: 0.92 }, { opacity: 0, scale: 2.1, duration: 0.9, ease: 'power2.out', immediateRender: false }, 'ignite+=0.12');

    // ---- 4) REVEAL: badge zooms THROUGH the camera with motion blur, columns wipe
    const ui = [bigCount, meta, progress, plus, crossH, crossV, pour, streak].filter(Boolean);
    bootLines.forEach(function (l) { ui.push(l); });
    tl.add(finish, 'ignite+=0.3');   /* perf 2026-09-07: was 0.6 — reveal hero sooner (LCP) */
    tl.add(function () { pl.classList.add('is-exiting'); }, '<');
    if (badge) tl.to(badge, { scale: 3.1, opacity: 0, filter: 'blur(14px)', duration: 0.6, ease: 'power3.in' }, '<');
    tl.to(ui, { opacity: 0, duration: 0.3, ease: 'power2.in' }, '<');
    if (words[wi]) tl.to(words[wi], { opacity: 0, duration: 0.25 }, '<');
    if (canvas) tl.to(canvas, { opacity: 0, duration: 0.4 }, '<0.1');
    tl.to(cols, {
      yPercent: -101, duration: 0.5, ease: 'power4.inOut', stagger: 0.05,   /* perf 2026-09-07: was 0.7/0.07 — faster uncover */
      onComplete: function () {
        if (ambient) ambient.kill();
        if (px) px.stop();
        clearTimeout(failsafe);
        pl.style.display = 'none';
      }
    }, '>-0.35');
  }
  /* === END ADDON: IGNITION v3 badge loader === */

  /* === ADDON: cinematic "IGNITION" preloader timeline ===
     A lime scanline sweeps down and reveals the brand logo (clip-path)
     while a ghost plus-sign motif slowly breathes behind it. An
     oversized counter runs 0→100 (bottom-right) driving the hairline
     progress bar and rotating brand words. At 100% the logo gets a
     radial flash + scale punch, the UI fades and the five background
     columns wipe upward — revealing the page underneath as the hero
     intro (finish) takes over. */
  function runCinematic(pl, bar, count, finish, failsafe) {
    const words = pl.querySelectorAll('.cine-words span');
    const logo = pl.querySelector('#ign-logo');
    const beam = pl.querySelector('#ign-beam');
    const flash = pl.querySelector('#ign-flash');
    const plus = pl.querySelector('.ign-plus');
    const bigCount = pl.querySelector('.ign-count');
    const meta = pl.querySelector('.cine-meta');
    const stage = pl.querySelector('.ign-stage');
    const progress = pl.querySelector('.ign-progress');
    const cols = pl.querySelectorAll('.ign-bg span');

    gsap.set(words, { yPercent: 120, opacity: 0 });
    if (words[0]) gsap.set(words[0], { yPercent: 0, opacity: 1 });
    if (logo) {
      logo.style.clipPath = 'inset(0 0 100% 0)';
      logo.style.webkitClipPath = 'inset(0 0 100% 0)';
    }
    if (bigCount) gsap.set(bigCount, { yPercent: 30, opacity: 0 });
    if (meta) gsap.set(meta, { opacity: 0 });

    // Ambient: the ghost plus slowly rotates/breathes for the whole load.
    // transformOrigin must stay 50% 50% on top of the CSS translate(-50%,-50%),
    // so rotate via a CSS-variable-free gsap tween on the SVG itself.
    var ambient = null;
    if (plus) {
      gsap.set(plus, { xPercent: -50, yPercent: -50, scale: 0.94, rotate: 0, left: '50%', top: '50%', transformOrigin: '50% 50%' });
      ambient = gsap.to(plus, { rotate: 90, scale: 1.02, duration: 4.5, ease: 'none' });
    }

    const obj = { v: 0 };
    const reveal = { p: 0 };
    let wi = 0;
    const tl = gsap.timeline();

    // 1) Scanline reveal of the logo
    if (beam) tl.to(beam, { opacity: 1, duration: 0.15 }, 0.25);
    tl.to(reveal, {
      p: 100, duration: 0.85, ease: 'power3.inOut',
      onUpdate: function () {
        if (logo) {
          var c = 'inset(0 0 ' + (100 - reveal.p) + '% 0)';
          logo.style.clipPath = c;
          logo.style.webkitClipPath = c;
        }
        if (beam) beam.style.top = reveal.p + '%';
      }
    }, 0.25);
    if (beam) tl.to(beam, { opacity: 0, duration: 0.2 }, '>-0.05');

    // 2) Meta + counter slide in
    if (meta) tl.to(meta, { opacity: 1, duration: 0.5 }, 0.5);
    if (bigCount) tl.to(bigCount, { yPercent: 0, opacity: 1, duration: 0.6, ease: 'power4.out' }, 0.55);

    // 3) Count 0→100: drives digits, hairline bar and rotating words
    tl.to(obj, {
      v: 100, duration: 1.9, ease: 'power2.inOut',
      onUpdate: function () {
        const v = Math.round(obj.v);
        if (bar) bar.style.transform = 'scaleX(' + (v / 100) + ')';
        if (count) count.textContent = v;
        if (words.length > 1) {
          const target = Math.min(words.length - 1, Math.floor(v / (100 / words.length)));
          if (target !== wi) {
            gsap.to(words[wi], { yPercent: -120, opacity: 0, duration: 0.4, ease: 'power2.in' });
            gsap.fromTo(words[target], { yPercent: 120, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5, ease: 'power3.out' });
            wi = target;
          }
        }
      }
    }, 0.55);

    // 4) 100%: radial flash + scale punch on the logo
    if (flash) {
      tl.fromTo(flash, { opacity: 0 }, { opacity: 1, duration: 0.26, ease: 'power2.out' }, '>');
      tl.to(flash, { opacity: 0, duration: 0.3, ease: 'power2.in' }, '>-0.04');
    }
    if (logo) tl.fromTo(logo, { scale: 1 }, { scale: 1.05, yoyo: true, repeat: 1, duration: 0.16, ease: 'power2.inOut', transformOrigin: '50% 50%' }, '<');

    // 5) Exit: UI out, columns wipe up, page revealed underneath
    const ui = [stage, bigCount, meta, progress, plus].filter(Boolean);
    tl.add(finish, '+=0.1');
    tl.to(ui, { opacity: 0, duration: 0.3, ease: 'power2.in' }, '<');
    tl.to(cols, {
      yPercent: -101, duration: 0.7, ease: 'power4.inOut', stagger: 0.07,
      onComplete: function () {
        if (ambient) ambient.kill();
        clearTimeout(failsafe);
        pl.style.display = 'none';
      }
    }, '>-0.05');
  }
  /* === END ADDON: cinematic "IGNITION" preloader timeline === */

  /* --------------------------------------------------------
     HERO INTRO
  -------------------------------------------------------- */
  function heroIntro() {
    if (typeof window.__hubHeroIntro === 'function') window.__hubHeroIntro();
    if (!hasGSAP || reduceMotion) return;

    const lines = document.querySelectorAll('[data-hero-line]');
    const fadeSel = '.hero__tagline, .hero__cta, .hero__stats, .scroll-cue, .header__bar > *';

    // In the Shopify theme editor the preview iframe can load throttled/hidden,
    // which freezes these entrance tweens and traps the headline + copy off-screen.
    // Skip the hide entirely there so content is always visible while editing.
    if (window.Shopify && window.Shopify.designMode) {
      if (lines.length) gsap.set(lines, { clearProps: 'transform' });
      return;
    }

    // Only tween targets that exist on this page (hero markup is homepage-only)
    // — avoids GSAP "target not found" console warnings on shop/cart/etc.
    var fromIf = function (sel, vars) { if (document.querySelector(sel)) gsap.from(sel, vars); };

    if (lines.length) {
      gsap.set(lines, { yPercent: 110 });
      gsap.to(lines, { yPercent: 0, duration: 1.1, ease: 'power4.out', stagger: 0.12, delay: 0.05 });
    }

    fromIf('.hero__tagline', { opacity: 0, y: 16, duration: 0.9, delay: 0.5, ease: 'power2.out' });
    fromIf('.hero__cta', { opacity: 0, y: 20, duration: 0.9, delay: 0.7, ease: 'power2.out' });
    fromIf('.hero__stats', { opacity: 0, y: 20, duration: 0.9, delay: 0.85, ease: 'power2.out' });
    fromIf('.scroll-cue', { opacity: 0, duration: 0.8, delay: 1.1 });
    // OPACITY-ONLY, deliberately no `y` (2026-09-06). A `y` translate leaves a residual transform on
    // each `.header__bar > *` child, and `.nav` is one of them. A transform makes an element the
    // CONTAINING BLOCK for its absolutely-positioned descendants, so the mega panel's `width:100%`
    // then resolved against the 430px .nav instead of the full-width header — collapsing the dropdown
    // to nav-width and spilling its right columns onto the hero ("the border enclosing the words only
    // half appears, then snaps back"). The 2.5s failsafe cleared it eventually, which is the "snaps
    // back". Fading with no translate means .nav never becomes a containing block, so the panel is
    // full-bleed from the first frame. Verified live: clearing the .nav transform snaps the panel to
    // 0->viewport, perfectly aligned.
    fromIf('.header__bar > *', { opacity: 0, duration: 0.7, delay: 0.3, stagger: 0.06, ease: 'power2.out' });

    // Failsafe: never let an interrupted/throttled tween trap hero + header copy.
    gsap.delayedCall(2.5, function () {
      if (lines.length) gsap.set(lines, { clearProps: 'transform' });
      if (document.querySelector(fadeSel)) gsap.set(fadeSel, { clearProps: 'opacity,transform' });
    });
  }

  /* --------------------------------------------------------
     WORD ROTATOR (hero)
  -------------------------------------------------------- */
  function initRotator() {
    const roto = document.getElementById('roto');
    if (!roto || !hasGSAP || reduceMotion) return;
    const words = Array.from(roto.querySelectorAll('span[aria-hidden="true"]'));
    if (!words.length) return;
    const sr = document.getElementById('roto-sr');
    let idx = 0;
    gsap.set(words, { yPercent: 110, opacity: 0 });
    gsap.set(words[0], { yPercent: 0, opacity: 1 });
    if (sr) sr.textContent = words[0].textContent;
    let rotatorInterval = null;
    function step() {
      const cur = words[idx];
      const next = words[(idx + 1) % words.length];
      gsap.to(cur, { yPercent: -110, opacity: 0, duration: 0.55, ease: 'power3.inOut' });
      gsap.fromTo(next, { yPercent: 110, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.55, ease: 'power3.out' });
      idx = (idx + 1) % words.length;
      if (sr) sr.textContent = next.textContent;
    }
    function start() { if (!rotatorInterval) rotatorInterval = setInterval(step, 2200); }
    function stop() { if (rotatorInterval) { clearInterval(rotatorInterval); rotatorInterval = null; } }
    start();
    /* pause when the tab is hidden, RESUME when it returns (was previously one-way: frozen for the rest of the page's life) */
    document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
  }

  /* --------------------------------------------------------
     SPLIT HELPERS
  -------------------------------------------------------- */
  function splitWords(el) {
    const out = [];
    const nodes = Array.from(el.childNodes);
    el.innerHTML = '';
    nodes.forEach(function (node) {
      if (node.nodeType === 3) {            // text node
        node.textContent.split(/(\s+)/).forEach(function (chunk) {
          if (chunk.trim() === '') { el.appendChild(document.createTextNode(chunk)); return; }
          const s = document.createElement('span');
          s.className = 'word'; s.textContent = chunk;
          el.appendChild(s); out.push(s);
        });
      } else if (node.nodeType === 1) {     // element (e.g. <em>)
        const wrap = node.cloneNode(false);
        node.textContent.split(/(\s+)/).forEach(function (chunk) {
          if (chunk.trim() === '') { wrap.appendChild(document.createTextNode(chunk)); return; }
          const s = document.createElement('span');
          s.className = 'word'; s.textContent = chunk;
          wrap.appendChild(s); out.push(s);
        });
        el.appendChild(wrap);
      }
    });
    return out;
  }

  function splitLines(el) {
    // split on <br> into overflow-hidden line wrappers
    const html = el.innerHTML;
    const parts = html.split(/<br\s*\/?>/i);
    el.innerHTML = '';
    const inners = [];
    parts.forEach(function (p) {
      const line = document.createElement('span'); line.className = 'split-line';
      const inner = document.createElement('span'); inner.style.display = 'block';
      inner.innerHTML = p.trim();
      line.appendChild(inner); el.appendChild(line); inners.push(inner);
    });
    return inners;
  }

  /* --------------------------------------------------------
     SCROLL REVEALS
  -------------------------------------------------------- */
  function initReveals() {
    if (!hasGSAP || typeof ScrollTrigger === 'undefined') return;

    // Reduced motion, or the Shopify editor (where ScrollTrigger can't measure a
    // throttled iframe reliably): show everything, skip the scroll-gated hiding.
    if (reduceMotion || (window.Shopify && window.Shopify.designMode)) {
      // Only clear what the reveals animate. clearProps:'all' also wiped author
      // inline styles — it stripped the stats eyebrow's colour, leaving it at
      // 1.02:1 on the lime band.
      gsap.set('[data-reveal]', { clearProps: 'opacity,transform,filter' });
      return;
    }

    // headline line reveals
    document.querySelectorAll('[data-reveal="lines"]').forEach(function (el) {
      const inners = splitLines(el);
      gsap.set(inners, { yPercent: 110 });
      ScrollTrigger.create({
        trigger: el, start: 'top 85%', once: true,
        onEnter: function () { gsap.to(inners, { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.1 }); }
      });
    });

    // fade — settles into focus (subtle blur-in + lift)
    gsap.utils.toArray('[data-reveal="fade"]').forEach(function (el) {
      gsap.from(el, {
        opacity: 0, y: 28, filter: 'blur(8px)', duration: 1.0, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        onComplete: function () { el.style.filter = ''; }
      });
    });

    // rise (with stagger for siblings) — deeper lift + blur-in.
    // Use ScrollTrigger.batch so each card animates when IT actually enters
    // the viewport. The old per-parent trigger fired once on the grid top and
    // animated far-off-screen cards together, which could leave bottom-row
    // cards stranded at their translated "from" state (y:56) — overlapping
    // whatever followed the grid (e.g. the collection pagination). Batch can't
    // strand: any card still hidden when scrolled into view is revealed.
    const riseEls = gsap.utils.toArray('[data-reveal="rise"]');
    if (riseEls.length) {
      // Pop-into-place: deeper lift + slight shrink, settling with a back.out
      // overshoot so cards visibly LAND in their spot (owner ask 2026-07-17 —
      // the old flat power3 rise read too subtle at a glance).
      gsap.set(riseEls, { opacity: 0, y: 64, scale: 0.92, filter: 'blur(6px)' });

      // Mark an element the moment its reveal STARTS. Both failsafes below ask
      // "did the reveal play?" through this flag instead of inferring it from
      // how the element currently looks — see the note on failsafe 1.
      // NOTE: `data-reveal-played` cannot collide with the `[data-reveal]`
      // selectors used here and in hub-scrollfx.js — attribute selectors match
      // exact names, so `[data-reveal]` never matches `data-reveal-played`.
      const markPlayed = function (e) { e.setAttribute('data-reveal-played', ''); };
      const isPlayed = function (e) { return e.hasAttribute('data-reveal-played'); };
      const forceShow = function (e) {
        markPlayed(e);
        gsap.set(e, { clearProps: 'opacity,transform,filter' });
      };

      const playRise = function (batch) {
        batch.forEach(markPlayed);
        gsap.to(batch, {
          opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.85, ease: 'back.out(1.4)',
          stagger: 0.09, overwrite: true,
          onComplete: function () { batch.forEach(function (e) { e.style.filter = ''; }); }
        });
      };
      ScrollTrigger.batch(riseEls, { start: 'top 90%', once: true, onEnter: playRise });

      // Failsafe 1: any card in the viewport but not yet revealed shortly after
      // load (a missed/desynced trigger) is force-revealed.
      //
      // THE GUARD IS THE PLAYED FLAG, NOT OPACITY. Opacity answers the wrong
      // question: an element whose opacity has been restored by some other path
      // while its transform is still at the from-state (y:64, scale:0.92) reads
      // as already-revealed and gets skipped — leaving a card sitting visibly
      // shrunk and 64px low at full opacity, which is precisely the stranding
      // this block exists to prevent.
      window.addEventListener('load', function () {
        setTimeout(function () {
          ScrollTrigger.refresh();
          riseEls.forEach(function (e) {
            var r = e.getBoundingClientRect();
            var inView = r.top < window.innerHeight && r.bottom > 0;
            if (inView && !isPlayed(e)) forceShow(e);
          });
        }, 1200);
      });

      // Failsafe 2: failsafe 1 fires ONCE, and only for what was on screen at
      // that instant. A below-fold card whose batch trigger never fires — the
      // realistic cause being trigger positions desynced by a late layout shift
      // (lazy images, web fonts, an app embed injecting markup) — would sit at
      // opacity:0 FOREVER with nothing left to rescue it. Invisible content is
      // a worse failure than a missed animation, so every card gets a standing
      // second chance, not just the ones that happened to be in view at 1200ms.
      //
      // The 600ms delay means this can never pre-empt the real animation:
      // ScrollTrigger.batch marks the element played immediately on enter, far
      // inside that window, so in the normal case this observer only unobserves.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var e = entry.target;
            setTimeout(function () {
              if (!isPlayed(e)) forceShow(e);
              io.unobserve(e);
            }, 600);
          });
        });
        riseEls.forEach(function (e) { io.observe(e); });
      }
    }

    // manifesto word-by-word scrub
    document.querySelectorAll('[data-split-words]').forEach(function (el) {
      const words = splitWords(el);
      gsap.to(words, {
        color: function (i, t) { return t.closest('em') ? '#B7FF00' : '#EFEFEF'; },
        ease: 'none', stagger: 0.06,
        scrollTrigger: { trigger: el, start: 'top 75%', end: 'bottom 60%', scrub: true }
      });
    });
  }

  /* --------------------------------------------------------
     COUNTERS
  -------------------------------------------------------- */
  function initCounters() {
    if (!hasGSAP || typeof ScrollTrigger === 'undefined') return;
    document.querySelectorAll('[data-count]').forEach(function (el) {
      const end = parseFloat(el.getAttribute('data-count'));
      const suffix = el.getAttribute('data-suffix') || '';
      // Power-up feel: .stat blocks get a --chg (0→1) var that fills the
      // charge bar under the number in step with the count (loader language).
      const holder = el.closest('.stat');
      const obj = { v: 0 };
      // ZERO IT HERE, NOT IN THE MARKUP (2026-08-19). The Liquid used to hard-code >0< as the
      // element's text and keep the real figure only in data-count, so the number a visitor saw
      // depended entirely on this function running. It does not always run: initCounters() returns
      // early when GSAP or ScrollTrigger is missing, and production error logs show theme assets
      // (gsap.min.js among them) failing to load for real visitors on flaky mobile connections.
      // The result was a homepage announcing 0 clubs, 0 sports, 0 venues and 0 races — an empty
      // marketplace — to anyone whose JS did not arrive.
      // The markup now ships the TRUE number, and the count-up zeroes it at wire-up time instead.
      // Only on the animating path: the reduced-motion branch below writes the final value on
      // enter, so for those visitors the correct number should simply stay put.
      // 2026-10-26: no longer zeroed at wire-up. A counter below the fold sat at '0' until scrolled to,
      // so text readers, crawlers that render JS without scrolling, and AI fetchers saw '0 clubs, 0 sports'.
      // The true number now stays in the text until the counter enters view; the roll starts from 0 there.
      ScrollTrigger.create({
        trigger: el, start: 'top 90%', once: true,
        onEnter: function () {
          if (reduceMotion) { el.textContent = end + suffix; if (holder) holder.style.setProperty('--chg', 1); return; }
          el.textContent = '0' + suffix;
          gsap.to(obj, {
            v: end, duration: 1.6, ease: 'power2.out',
            onUpdate: function () {
              el.textContent = Math.round(obj.v) + suffix;
              if (holder && end) holder.style.setProperty('--chg', (obj.v / end).toFixed(3));
            }
          });
        }
      });
    });
  }

  /* --------------------------------------------------------
     MARQUEE
  -------------------------------------------------------- */
  function initMarquee() {
    if (!hasGSAP || reduceMotion) return;
    const items = [];
    document.querySelectorAll('[data-marquee]').forEach(function (track) {
      // mark bound so hub-effects' initExtraMarquee doesn't double-bind the same track
      track.setAttribute('data-marquee-bound', '1');
      const tween = gsap.to(track, { xPercent: -50, duration: 22, ease: 'none', repeat: -1 });
      const sec = track.closest('.ticker') || track;
      const it = { tween: tween, hover: false, skewTo: gsap.quickTo(track, 'skewX', { duration: 0.35, ease: 'power2.out' }) };
      sec.addEventListener('mouseenter', function () { it.hover = true; });
      sec.addEventListener('mouseleave', function () { it.hover = false; });
      items.push(it);
    });
    if (!items.length) return;
    // Velocity-reactive: the ticker whips (speed + skew) with scroll speed,
    // then settles. Uses raw scroll delta so it works with or without Lenis
    // (and with native touch scrolling on mobile).
    let lastY = window.scrollY || 0;
    let vel = 0;
    gsap.ticker.add(function () {
      const y = lenis ? lenis.scroll : (window.scrollY || 0);
      vel += ((y - lastY) - vel) * 0.12;
      lastY = y;
      const boost = Math.min(Math.abs(vel) * 0.06, 3);
      const skew = Math.max(-9, Math.min(9, vel * 0.22));
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        const target = it.hover ? 0.25 : 1 + boost;
        it.tween.timeScale(it.tween.timeScale() + (target - it.tween.timeScale()) * 0.08);
        it.skewTo(it.hover ? 0 : skew);
      }
    });
  }

  /* --------------------------------------------------------
     HEADER show/hide
  -------------------------------------------------------- */
  function initHeader() {
    const header = document.getElementById('header');
    if (!header) return;
    let last = 0;
    function update(y) {
      if (y > 40) header.classList.add('is-scrolled'); else header.classList.remove('is-scrolled');
      if (y > last && y > 400 && !document.body.classList.contains('menu-open')) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      last = y;
    }
    if (lenis) lenis.on('scroll', function (e) { update(e.scroll); });
    else window.addEventListener('scroll', function () { update(window.scrollY); }, { passive: true });
  }

  /* --------------------------------------------------------
     MOBILE MENU
  -------------------------------------------------------- */
  const menu = document.getElementById('mobileMenu');
  const burger = document.getElementById('burger');
  function closeMobile() {
    if (!menu || !menu.classList.contains('is-open')) return;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
    if (burger) burger.setAttribute('aria-expanded', 'false');
    if (lenis) lenis.start();
    /* ADDON (a11y 2026-08-06): send focus back where it came from. Without this a
       keyboard user who closes the menu is left with focus on a node inside a now
       aria-hidden dialog, and the next Tab restarts from the top of the document. */
    if (burger && document.activeElement && menu.contains(document.activeElement)) burger.focus();
  }
  function initMobileMenu() {
    if (!menu || !burger) return;
    burger.addEventListener('click', function () {
      const open = menu.classList.toggle('is-open');
      menu.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('menu-open', open);
      burger.setAttribute('aria-expanded', String(open));
      if (lenis) { open ? lenis.stop() : lenis.start(); }
      /* focus management */
      if (open) { setTimeout(function () { menu.focus(); }, 100); }
    });
    /* close on overlay click (click on the menu backdrop itself) */
    menu.addEventListener('click', function (e) { if (e.target === menu) closeMobile(); });
    /* ADDON (a11y 2026-08-06): Escape moved from the MENU to the DOCUMENT.
       Bound to the menu it could never fire. The listener only receives a keydown
       if focus is inside the menu, and focus was never put there — menu.focus()
       silently did nothing because the dialog had no tabindex (now added in
       header.liquid). So Escape, the one thing every other modal on this site
       honours, was dead on the primary mobile navigation.
       document-level is also simply the house pattern: hub-membership.js,
       hub-rewards.js and hub-shop.js all bind it here, guarded on open state. */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMobile();
    });
    menu.querySelectorAll('[data-acc]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const sub = btn.nextElementSibling;
        const open = sub.classList.toggle('is-open');
        btn.querySelector('span:last-child').textContent = open ? '–' : '+';
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }

  /* --------------------------------------------------------
     SHOP FILTER CHIPS (visual)
  -------------------------------------------------------- */
  function initChips() {
    const chips = document.querySelectorAll('.shop__filters .chip');
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
        chip.setAttribute('aria-pressed', 'true');
      });
    });
  }

  /* --------------------------------------------------------
     CUSTOM CURSOR + MAGNETIC
  -------------------------------------------------------- */
  function initCursor() {
    const cursor = document.getElementById('cursor');
    if (!finePointer || reduceMotion) {
      if (cursor) cursor.style.display = 'none';
      document.body.style.cursor = '';
      return;
    }
    document.body.style.cursor = 'none';
    const ring = document.getElementById('cursor-ring');
    const dot = document.getElementById('cursor-dot');
    const lbl = document.getElementById('cursor-lbl');

    // Hold the custom cursor hidden until the page has finished loading AND the
    // pointer actually moves — so the ring never sits parked at screen-centre
    // over the preloader. Revealed once, on the first post-load pointermove.
    document.body.classList.add('cursor-preinit');
    let loadDone = document.readyState === 'complete', firstMove = false;
    function revealCursor() { if (loadDone && firstMove) document.body.classList.remove('cursor-preinit'); }
    if (!loadDone) window.addEventListener('load', function () { loadDone = true; revealCursor(); });
    window.addEventListener('pointermove', function () { firstMove = true; revealCursor(); }, { passive: true });

    // Energy "wake": a soft lime halo that trails the pointer (created here so
    // no markup change is needed — works on every page that loads hub-app.js).
    const glow = document.createElement('div');
    glow.id = 'cursor-glow';
    glow.setAttribute('aria-hidden', 'true');
    document.body.appendChild(glow);

    let tx = window.innerWidth / 2, ty = window.innerHeight / 2;  // raw pointer
    let rx = tx, ry = ty;                                         // ring (eased)
    let gx = tx, gy = ty;                                         // glow (heavy lag)
    let px = tx, py = ty;                                         // previous pointer
    let speed = 0;
    let ringK = 1, dotK = 1;                                      // size factors, eased in loop()
    let magnet = null;                                            // {x,y} of a docked control
    let magnetEl = null, hoverEl = null;                          // tracked so DOM removal can't strand state

    window.addEventListener('pointermove', function (e) {
      tx = e.clientX; ty = e.clientY;
      // The dot's transform used to be written here. It moved into loop() so its press
      // scale can animate while the pointer is STATIONARY — which is exactly when a press
      // happens. No visual change from the move: nothing paints between a pointermove and
      // the next frame, so writing it per-move was strictly more work for the same result.
    }, { passive: true });

    window.addEventListener('pointerdown', function () { document.body.classList.add('cursor-down'); });
    window.addEventListener('pointerup', function () { document.body.classList.remove('cursor-down'); });

    // Click ripple — a lime ring blooms from the click point and fades.
    window.addEventListener('pointerdown', function (e) {
      var rp = document.createElement('span');
      rp.className = 'cursor-ripple';
      rp.style.left = e.clientX + 'px';
      rp.style.top = e.clientY + 'px';
      document.body.appendChild(rp);
      setTimeout(function () { rp.remove(); }, 620);
    });

    // Idle "breathing" — after a short pause the cursor gently pulses (feels alive).
    var idleTimer = null;
    function wake() {
      document.body.classList.remove('cursor-idle');
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { document.body.classList.add('cursor-idle'); }, 1400);
    }
    window.addEventListener('pointermove', wake, { passive: true });
    wake();

    function loop() {
      // Safety: if a hovered/docked control was removed from the DOM mid-hover
      // (e.g. the quiz re-rendering after an answer), its mouseleave never fires —
      // so clear the stranded state here instead of letting the cursor "stick".
      if (magnetEl && !document.body.contains(magnetEl)) { magnetEl = null; magnet = null; }
      if (hoverEl && !document.body.contains(hoverEl)) { hoverEl = null; document.body.classList.remove('cursor-hover'); if (lbl) lbl.textContent = ''; }
      // Magnetic docking: gently bias the ring toward the centre of a hovered
      // control (kept light so it never feels like it's fighting the pointer).
      let aimX = tx, aimY = ty;
      if (magnet) { aimX = tx + (magnet.x - tx) * 0.22; aimY = ty + (magnet.y - ty) * 0.22; }
      // Tighter follow — the ring tracks closely (snappy), the glow trails softly.
      rx += (aimX - rx) * 0.38;  ry += (aimY - ry) * 0.38;
      gx += (tx - gx) * 0.16;    gy += (ty - gy) * 0.16;

      // Velocity → a subtle squash & stretch along the direction of travel.
      const vx = tx - px, vy = ty - py; px = tx; py = ty;
      speed += (Math.min(Math.hypot(vx, vy), 90) - speed) * 0.18;
      const stretch = speed / 90;
      let morph = '';
      if (!document.body.classList.contains('cursor-hover')) {   // don't distort the labelled bubble
        const ang = speed > 1.4 ? Math.atan2(vy, vx) * 180 / Math.PI : 0;
        morph = ' rotate(' + ang.toFixed(2) + 'deg) scale(' + (1 + stretch * 0.32).toFixed(3) + ',' + (1 - stretch * 0.16).toFixed(3) + ')';
      }
      // Size states are scale factors composed into these strings, never width/height —
      // width/height invalidate layout on every frame of the transition. The factors have
      // to live here rather than in CSS because both inline transforms are rewritten
      // continuously, so a CSS transform on either element is always overridden.
      // Factors preserve the old pixel sizes exactly: ring 74/38 = 1.947 hovered,
      // 30/38 = 0.789 pressed; dot 13/7 = 1.857 pressed.
      var ringTo = document.body.classList.contains('cursor-hover') ? 1.947
                 : document.body.classList.contains('cursor-down')  ? 0.789
                 : 1;
      var dotTo  = document.body.classList.contains('cursor-down')  ? 1.857 : 1;
      // Lerps replace the CSS transitions they removed, matched to their old durations:
      // 0.18/frame reaches 99% in ~23 frames (~390ms, was 0.35s), 0.26 in ~15 (~250ms).
      ringK += (ringTo - ringK) * 0.18;
      dotK  += (dotTo  - dotK)  * 0.26;
      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px) translate(-50%,-50%) scale(' + ringK.toFixed(3) + ')' + morph;
      dot.style.transform  = 'translate(' + tx + 'px,' + ty + 'px) translate(-50%,-50%) scale(' + dotK.toFixed(3) + ')';
      glow.style.transform = 'translate(' + gx + 'px,' + gy + 'px) translate(-50%,-50%) scale(' + (0.8 + stretch * 0.55).toFixed(3) + ')';
      requestAnimationFrame(loop);
    }
    loop();

    // Delegated hover + magnetic docking — works for elements added/removed at
    // runtime (the quiz, cart drawer, etc.) and never strands state on a node
    // that gets replaced while hovered. Pairs with the pull in initMagnetic().
    var HOVER_SEL = '[data-cursor]', DOCK_SEL = '.btn, .icon-btn, [data-magnetic]';
    document.addEventListener('pointermove', function (e) {
      var t = e.target;
      var h = t && t.closest ? t.closest(HOVER_SEL) : null;
      if (h !== hoverEl) {
        hoverEl = h;
        document.body.classList.toggle('cursor-hover', !!h);
        if (lbl) lbl.textContent = h ? (h.getAttribute('data-cursor') || '') : '';
      }
      var d = t && t.closest ? t.closest(DOCK_SEL) : null;
      magnetEl = d;
      if (d) { var r = d.getBoundingClientRect(); magnet = { x: r.left + r.width / 2, y: r.top + r.height / 2 }; }
      else magnet = null;
    }, { passive: true });

    document.addEventListener('mouseleave', function () { document.body.classList.add('cursor-hidden'); });
    document.addEventListener('mouseenter', function () { document.body.classList.remove('cursor-hidden'); });

    // ---- Native-cursor fallback over third-party iframes / overlays ----
    // The custom cursor is a fixed element that CANNOT render over a cross-origin
    // iframe (Smile.io "Rewards" panel, embedded widgets) and the page also forces
    // `cursor:none`, so over those the user sees NO cursor. Detect when such an
    // overlay is on top and hand control back to the real OS cursor.
    function overlayOnTop() {
      // True only when a LARGE fixed iframe panel is actually open over the page
      // (Smile rewards panel, a full-screen widget/modal). Deliberately excludes
      // the small always-present Smile launcher tab and in-flow embeds (maps/video,
      // which are position:absolute/static), so the custom cursor stays normal.
      var vw = innerWidth, vh = innerHeight;
      var frames = document.getElementsByTagName('iframe');
      for (var i = 0; i < frames.length; i++) {
        var f = frames[i], cs = getComputedStyle(f);
        if (cs.position !== 'fixed') continue; // panels/modals are fixed; embeds are not
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;
        var r = f.getBoundingClientRect();
        if (r.bottom <= 0 || r.right <= 0 || r.top >= vh || r.left >= vw) continue; // off-screen
        if (Math.min(r.width, r.height) > 300) return true; // big enough to be a panel, not the launcher
      }
      return false;
    }
    function syncCursorMode() {
      var native = overlayOnTop();
      document.body.classList.toggle('native-cursor', native);
      document.body.style.cursor = native ? '' : 'none'; // '' = let the OS cursor show
    }
    // Re-evaluate on the signals that accompany a widget opening/closing. (The always-on
    // MutationObserver + 700ms interval that watched for the Smile.io iframe were removed
    // 2026-07-24 with Smile — they were per-frame churn on this GSAP-heavy site; the cheap
    // focus/blur/click sync below covers any remaining fixed-iframe overlay.)
    window.addEventListener('blur', function () { document.body.classList.add('native-cursor'); document.body.style.cursor = ''; });
    window.addEventListener('focus', syncCursorMode);
    document.addEventListener('click', function () { setTimeout(syncCursorMode, 60); });
  }

  function initMagnetic() {
    if (!finePointer || reduceMotion || !hasGSAP) return;
    document.querySelectorAll('.btn, .icon-btn').forEach(function (el) {
      const strength = el.classList.contains('icon-btn') ? 0.4 : 0.25;
      el.addEventListener('mousemove', function (e) {
        const r = el.getBoundingClientRect();
        const mx = e.clientX - (r.left + r.width / 2);
        const my = e.clientY - (r.top + r.height / 2);
        gsap.to(el, { x: mx * strength, y: my * strength, duration: 0.4, ease: 'power3.out' });
      });
      el.addEventListener('mouseleave', function () {
        gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1,0.4)' });
      });
    });
  }

  /* --------------------------------------------------------
     SPARK BURST (data-spark) — lime sparks fly off a control on
     hover (fine pointers) and on click/tap (all devices). Reuses
     the loader's energy language on key CTAs.
  -------------------------------------------------------- */
  function initSparks() {
    if (!hasGSAP || reduceMotion) return;
    document.querySelectorAll('[data-spark]').forEach(function (btn) {
      let last = 0;
      function burst() {
        const now = Date.now();
        if (now - last < 700) return; // throttle repeat hovers
        last = now;
        const r = btn.getBoundingClientRect();
        for (let i = 0; i < 12; i++) {
          const s = document.createElement('i');
          s.className = 'spark';
          s.style.left = (r.left + r.width / 2) + 'px';
          s.style.top = (r.top + r.height / 2) + 'px';
          document.body.appendChild(s);
          const a = Math.random() * Math.PI * 2;
          const d = 42 + Math.random() * 72;
          gsap.fromTo(s,
            { x: 0, y: 0, scale: 0.6 + Math.random() * 0.8, opacity: 1 },
            { x: Math.cos(a) * d, y: Math.sin(a) * d, scale: 0.1, opacity: 0,
              duration: 0.55 + Math.random() * 0.35, ease: 'power2.out',
              onComplete: function () { s.remove(); } });
        }
      }
      if (finePointer) btn.addEventListener('mouseenter', burst);
      btn.addEventListener('click', burst);
    });
  }

  /* --------------------------------------------------------
     FOOTER — live Singapore clock ([data-sgt])
  -------------------------------------------------------- */
  function initClock() {
    const el = document.getElementById('footer-clock');
    if (!el) return;
    let fmt;
    try { fmt = new Intl.DateTimeFormat('en-SG', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Asia/Singapore' }); }
    catch (e) { return; }
    function tick() { el.textContent = 'SGP ' + fmt.format(new Date()); }
    tick();
    setInterval(tick, 1000);
  }

  /* --------------------------------------------------------
     ECO CARD spotlight (follow cursor glow)
  -------------------------------------------------------- */
  function initSpotlight() {
    if (!finePointer) return;
    document.querySelectorAll('.eco-card, .media-card').forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
        card.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100) + '%');
      });
    });
  }

  /* --------------------------------------------------------
     BOOT
  -------------------------------------------------------- */
  function boot() {
    /* Each init runs in isolation so a missing element on one page can never
       stop the rest of the site's JS from initialising. */
    var inits = [
      initLenis, initHeader, initMobileMenu, initChips, initReveals,
      initCounters, initMarquee, initRotator, initCursor, initMagnetic,
      initSpotlight, initSparks, initClock
    ];
    for (var i = 0; i < inits.length; i++) {
      try { inits[i](); }
      catch (err) { if (window.console && console.warn) { console.warn('[hub] init skipped:', (inits[i] && inits[i].name) || i, err); } }
    }

    try { runPreloader(function () { heroIntro(); }); }
    catch (err) { if (window.console && console.warn) { console.warn('[hub] preloader skipped:', err); } }

    if (typeof ScrollTrigger !== 'undefined') {
      window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
